import { useRouter } from "next/router";
import {
  TextAndList as TextAndListProps,
  TextAndList_c2A,
} from "../../../../__generated__/graphql";
import gql from "graphql-tag";
import { ContentfulComponentType } from "../types";
import TextFieldsV2 from "../TextFieldsV2";
import Gutter from "ui-kit/Gutter";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTitle,
} from "ui-kit";
import styles from "./ContentfulTextAndListV2.module.css";

const getC2AHref = (
  destination: NonNullable<TextAndList_c2A["destination"]>
): string => {
  if (destination.__typename === "Page") return destination.slug ?? "";
  if (destination.__typename === "Url") return destination.url ?? "";
  return "";
};

export const ContentfulTextAndListV2: ContentfulComponentType<
  TextAndListProps
> = ({ title, intro, text, listItemsCollection, listLayout, c2A, layout }) => {
  const router = useRouter();
  const listItems = listItemsCollection?.items ?? [];
  const c2aHref = c2A?.destination ? getC2AHref(c2A.destination) : "";

  return (
    <div className="container">
      <div className={layout! ? styles.containerReverse : styles.container}>
        <div className={styles.textContainer}>
          <TextFieldsV2
            title={title}
            text={text}
            intro={intro}
            variantText="bodyRg"
            gutterBottom={0}
          />
          {c2A?.title && c2aHref && (
            <>
              <Gutter size={4} />
              <a
                className={styles.link}
                href={c2aHref}
                onClick={(e) => {
                  e.preventDefault();
                  router.push(c2aHref);
                }}
              >
                {c2A.title}
              </a>
            </>
          )}
        </div>
        <div className={styles.listContainer}>
          {listLayout ? (
            <Accordion collapsible multiple>
              {listItems
                .flatMap((item) => (item ? [item] : []))
                .map((item) => (
                  <AccordionItem
                    key={item.title}
                    className={styles.accordionItem}
                  >
                    <AccordionTitle className={styles.accordionTitle}>
                      <span className={styles.accordionTitleText}>
                        {item.title}
                      </span>
                    </AccordionTitle>
                    <AccordionPanel>
                      {item.text && (
                        <div className={styles.answerText}>
                          <TextFieldsV2
                            text={item.text}
                            variantText="bodyRg"
                            gutterGap={0}
                            gutterBottom={0}
                          />
                        </div>
                      )}
                    </AccordionPanel>
                  </AccordionItem>
                ))}
            </Accordion>
          ) : (
            <ul className={styles.list}>
              {listItems
                .flatMap((item) => (item ? [item] : []))
                .map((item) => (
                  <li key={item.title} className={styles.listItem}>
                    <div className={styles.listItemTitle}>{item.title}</div>
                    {item.text && (
                      <div className={styles.listItemText}>
                        <TextFieldsV2
                          text={item.text}
                          variantText="bodyRg"
                          gutterGap={0}
                          gutterBottom={0}
                        />
                      </div>
                    )}
                  </li>
                ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

ContentfulTextAndListV2.fragment = gql`
  fragment TextAndList on ComponentTextAndList {
    highlight
    title
    intro
    text
    theme
    layout
    listItemsCollection(limit: 10) {
      items {
        title
        text
        icon
      }
    }
    listLayout
    listStyle
    c2A {
      ...C2A
    }
    list
    sys {
      id
      publishedAt
    }
  }
`;
